import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { ImageOff } from 'lucide-react';

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  fallback?: string;
  aspectRatio?: 'square' | 'video' | 'portrait' | 'auto';
  blur?: boolean;
  threshold?: number;
}

/**
 * Lazy loading image component with blur-up effect and error handling
 */
export function LazyImage({
  src,
  alt,
  fallback,
  aspectRatio = 'auto',
  blur = true,
  threshold = 0.1,
  className,
  ...props
}: LazyImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '50px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  const handleLoad = () => {
    setIsLoaded(true);
    setHasError(false);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  const aspectRatioClasses = {
    square: 'aspect-square',
    video: 'aspect-video',
    portrait: 'aspect-[3/4]',
    auto: '',
  };

  if (hasError) {
    return (
      <div
        className={cn(
          'flex items-center justify-center bg-muted rounded-lg',
          aspectRatioClasses[aspectRatio],
          className
        )}
      >
        {fallback ? (
          <img
            src={fallback}
            alt={alt}
            className={cn('w-full h-full object-cover', className)}
            {...props}
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-muted-foreground p-4">
            <ImageOff className="h-8 w-8" />
            <span className="text-xs text-center">Failed to load image</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={imgRef}
      className={cn(
        'relative overflow-hidden bg-muted rounded-lg',
        aspectRatioClasses[aspectRatio],
        className
      )}
    >
      {/* Placeholder skeleton */}
      {!isLoaded && (
        <div className="absolute inset-0 animate-pulse motion-reduce:animate-none bg-gradient-to-r from-muted via-muted/80 to-muted" />
      )}

      {/* Actual image */}
      {isInView && (
        <img
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
          decoding="async"
          className={cn(
            'w-full h-full object-cover transition-all duration-500 motion-reduce:transition-none',
            !isLoaded && blur && 'blur-sm scale-105 motion-reduce:blur-0 motion-reduce:scale-100',
            isLoaded && 'blur-0 scale-100',
            !isLoaded && 'opacity-0',
            isLoaded && 'opacity-100'
          )}
          {...props}
        />
      )}
    </div>
  );
}

/**
 * Responsive image component with srcset support
 */
interface ResponsiveImageProps extends Omit<LazyImageProps, 'srcSet'> {
  srcSet?: { src: string; width: number }[];
  sizes?: string;
}

export function ResponsiveImage({
  src,
  srcSet,
  sizes = '100vw',
  ...props
}: ResponsiveImageProps) {
  const srcSetString = srcSet
    ?.map(({ src, width }) => `${src} ${width}w`)
    .join(', ');

  return (
    <LazyImage
      src={src}
      {...props}
    />
  );
}
